import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CommonState, error, isFetching } from '@pec/aion-ui-core/slices/common';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';

const name = 'programReviews';

export const fetchProgramReviewsCount = createAsyncThunk(
    `${name}/fetchCount`,
    async (organizationId: string, { dispatch }) => {
        try {
            const filter = `contractorId eq ${organizationId} and (status eq 'Accepted' or status eq 'Completable' or status eq 'CompletableNotApplicable' or status eq 'Incomplete')`;
            const listUrl = `/api/v3.01/safetyProgramRequirements?$filter=${filter.replace(/ /g, '+')}&$expand=safetyProgramRequirementValidation`;

            const listResponse = await axios.get(listUrl);
            const items: Array<any> = listResponse?.data?.value ?? listResponse?.data ?? [];

            const count = items.reduce((acc, item) => {
                // For "Accepted" status, check if needsReacceptance is true
                if (item.status === 'Accepted') {
                    const v = item.safetyProgramRequirementValidation;
                    const needsReacceptance = v && v.needsReacceptance === true;
                    return acc + (needsReacceptance ? 1 : 0);
                }
                // For "Completable", "CompletableNotApplicable" and "Incomplete" statuses, always count them
                if (item.status === 'Completable' || item.status === 'CompletableNotApplicable' || item.status === 'Incomplete') {
                    return acc + 1;
                }
                return acc;
            }, 0);

            return count;
        } catch (error) {
            dispatch(enqueueRequestErrorSnackbar());
            throw error;
        }
    }
);

export type State = CommonState & {
    count: number;
};

const initialState: State = {
    count: 0,
    isFetching: false,
    error: null
};

const programReviews = createSlice({
    name,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchProgramReviewsCount.pending, isFetching)
            .addCase(fetchProgramReviewsCount.fulfilled, (state, action) => {
                state.count = action.payload;
                state.isFetching = false;
            })
            .addCase(fetchProgramReviewsCount.rejected, error);
    }
});

export const { reducer: programReviewsReducer } = programReviews;
